'use client';

export type ConsentRegion = 'gdpr' | 'ccpa' | 'none';

const APP_NAME = 'bearsenergy';
const CONSENT_KEY = APP_NAME.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_') + '_consent_v1';

export interface ConsentRecord {
  version: '1.0';
  timestamp: string; // ISO 8601
  region: ConsentRegion;
  choices: {
    necessary: true;
    analytics: boolean;
    marketing: boolean;
    preferences: boolean;
  };
  userAgent: string;
}

/**
 * Detect user region based on browser locale and timezone
 * No external API calls - uses navigator.language and Intl API
 */
export function detectRegion(): ConsentRegion {
  if (typeof navigator === 'undefined') return 'gdpr'; // SSR default

  const lang = navigator.language || '';
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';

  const euTZPrefixes = [
    'Europe/',
    'Atlantic/Azores',
    'Atlantic/Canary',
    'Atlantic/Faroe',
    'Atlantic/Madeira',
    'Arctic/Longyearbyen',
  ];
  const usTZPrefixes = ['America/'];
  const euCountryCodes = [
    'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI',
    'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT',
    'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK', 'GB', 'IS', 'LI',
    'NO', 'CH', 'BR', // Brazil for LGPD
  ];

  const isEU = euTZPrefixes.some((p) => tz.startsWith(p))
    || euCountryCodes.some((c) => lang.toUpperCase().endsWith('-' + c));
  const isUS = usTZPrefixes.some((p) => tz.startsWith(p))
    && (lang.startsWith('en-US') || lang.startsWith('en-CA'));

  if (isEU) return 'gdpr';
  if (isUS) return 'ccpa';
  return 'none';
}

/**
 * Get current consent record from localStorage
 */
export function getConsent(): ConsentRecord | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as ConsentRecord;
  } catch (error) {
    console.error('Error reading consent from localStorage:', error);
    return null;
  }
}

/**
 * Save consent record to localStorage
 */
export function setConsent(choices: ConsentRecord['choices'], region: ConsentRegion): ConsentRecord {
  if (typeof window === 'undefined') {
    throw new Error('setConsent must be called in browser context');
  }

  const record: ConsentRecord = {
    version: '1.0',
    timestamp: new Date().toISOString(),
    region,
    choices: {
      necessary: true, // Always true
      analytics: choices.analytics,
      marketing: choices.marketing,
      preferences: choices.preferences,
    },
    userAgent: navigator.userAgent,
  };

  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  } catch (error) {
    console.error('Error saving consent to localStorage:', error);
  }

  return record;
}

/**
 * Check if a specific consent category is enabled
 */
export function isConsentGiven(category: keyof ConsentRecord['choices']): boolean {
  const consent = getConsent();
  if (!consent) return category === 'necessary'; // Necessary is always true by default
  return consent.choices[category];
}

/**
 * Clear consent record from localStorage
 */
export function clearConsent(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch (error) {
    console.error('Error clearing consent from localStorage:', error);
  }
}

/**
 * Log consent action for audit trail
 */
export function logConsentAction(
  action: 'accept_all' | 'reject_all' | 'save_preferences' | 'dismiss',
  region: ConsentRegion,
  choices?: ConsentRecord['choices']
): void {
  if (typeof window === 'undefined') return;

  const logEntry = {
    timestamp: new Date().toISOString(),
    action,
    region,
    choices,
    url: window.location.href,
  };

  console.log('[Consent Log]', logEntry);
}
